import React, { useEffect, useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { SlArrowRight } from "react-icons/sl";
import { formatAmount } from "../../../../utils/formatTime";
import toast from "react-hot-toast";
import GetRates from "../../../../components/utils/GetRates";
import { useUser } from "../../../../context/user-context";
import useAuthAxios from "../../../../utils/baseAxios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API } from "../../../../constants/api";
import { errorMessage } from "../../../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import { FaCaretDown } from "react-icons/fa";
import { RiSearchLine } from "react-icons/ri";
import {
  electricUtils,
  NAIRA_WALLET_ENABLED,
} from "../../../../constants/constants";
import ProviderAvatar from "../../../../components/ProviderAvatar";
import { ElectricityPaymentSource, ElectricityProvider } from "./types";
import PaymentSourceSelect from "../PaymentSourceSelect";

type BuyElectricityProps = {
  setOpenElectric: (open: boolean) => void;
  setOpenElectricPreview: (open: boolean) => void;
  meterNo: string;
  setMeterNo: (value: string) => void;
  providerName: string;
  setProviderName: (value: string) => void;
  productSlug: string;
  setProductSlug: (value: string) => void;
  electricPhone: string;
  setElectricPhone: (value: string) => void;
  electricAmount: string;
  setElectricAmount: (value: string) => void;
  paymentSource: ElectricityPaymentSource;
  setPaymentSource: (value: ElectricityPaymentSource) => void;
  setElectricCustomerName: (value: string) => void;
};

// Bundled logos first, then the backend's logoUrl. Returns "" when neither
// exists — ProviderAvatar then shows the provider's initials instead.
const getProviderLogo = (slug: string, logoUrl: string | null) => {
  const keyword = slug.split("_")[0];
  const match = electricUtils.find(
    (item) =>
      item.serviceId?.toLowerCase().includes(keyword) ||
      item.name?.toLowerCase().includes(keyword)
  );
  return match?.logo ?? logoUrl ?? "";
};

const BuyElectricity: React.FC<BuyElectricityProps> = ({
  setOpenElectric,
  setOpenElectricPreview,
  meterNo,
  setMeterNo,
  providerName,
  setProviderName,
  productSlug,
  setProductSlug,
  electricPhone,
  setElectricPhone,
  electricAmount,
  setElectricAmount,
  paymentSource,
  setPaymentSource,
  setElectricCustomerName,
}) => {
  const [openProvider, setOpenProvider] = useState(false);
  const [providerSearch, setProviderSearch] = useState("");
  const [verifiedCustomer, setVerifiedCustomer] = useState<{
    customerName?: string;
    meterNumber?: string;
  } | null>(null);
  const [displayAmount, setDisplayAmount] = useState("");

  const { userDetails } = useUser();
  const walletBalances = userDetails?.user.deexWalletBalances;
  const axiosInstance = useAuthAxios();

  const usdtBalance = walletBalances?.usdt ?? 0;
  const ngnBalance = walletBalances?.ngn ?? 0;
  const usdtRate = GetRates("USDT");

  // Whole naira only — the purchase endpoint's `amount` is an integer.
  const isNumber = (str: string) => /^\d*$/.test(str);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, "");
    if (isNumber(value)) {
      setElectricAmount(value);
      setDisplayAmount(value);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (electricAmount) {
        setDisplayAmount(Number(electricAmount).toLocaleString("en-US"));
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [electricAmount]);

  const {
    data: providersResponse,
    isLoading: loadingProviders,
    isError,
  } = useQuery({
    queryKey: ["electricity-providers"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getElectricityProviders);
      return response.data;
    },
    retry: 1,
  });

  const providers: ElectricityProvider[] = useMemo(
    () => providersResponse?.data ?? [],
    [providersResponse?.data]
  );

  const filteredProviders = useMemo(() => {
    const query = providerSearch.trim().toLowerCase();
    if (!query) return providers;
    return providers.filter(
      (provider) =>
        provider.name.toLowerCase().includes(query) ||
        provider.slug.toLowerCase().includes(query)
    );
  }, [providers, providerSearch]);

  const selectedProvider = useMemo(
    () =>
      providers.find((p) => p.products.some((prod) => prod.id === productSlug)),
    [providers, productSlug]
  );

  const isBalanceSufficient =
    paymentSource === "NGN"
      ? Number(electricAmount) <= Number(ngnBalance)
      : Number(electricAmount) / Number(usdtRate) <= Number(usdtBalance);

  const validate = () => {
    // API minimum for electricity is ₦500.
    if (!electricAmount || Number(electricAmount) < 500) {
      toast.error("Minimum purchase for electricity is 500 NGN");
      return false;
    }
    if (!meterNo) {
      toast.error("Kindly enter a valid meter number");
      return false;
    }
    if (!electricPhone || electricPhone.length < 11) {
      toast.error("Kindly enter a valid phone number");
      return false;
    }
    if (!providerName || !productSlug) {
      toast.error("Kindly select a service provider");
      return false;
    }
    if (!isBalanceSufficient) {
      toast.error("Insufficient funds");
      return false;
    }
    return true;
  };

  const verifyMeter = async (data: {
    meterNumber: string;
    providerSlug: string;
  }) => {
    const response = await axiosInstance.post(API.electricityVerify, data);
    return response.data;
  };

  const completeCheck = useMutation({
    mutationFn: verifyMeter,
    onSuccess: (response) => {
      toast.success(response?.message ?? "Meter verified");
      if (response?.data) {
        setVerifiedCustomer({
          customerName: response.data.customerName,
          meterNumber: response.data.meterNumber,
        });
      }
    },
    onError: (error: { data?: { message?: string }; message?: string }) => {
      toast.error(
        errorMessage(
          error?.data?.message || error?.message || "Verification failed"
        )
      );
    },
  });

  const handleProviderSelect = (provider: ElectricityProvider) => {
    const product = provider.products[0];
    setProviderName(provider.name);
    setProductSlug(product?.id ?? "");
    setVerifiedCustomer(null);
    setProviderSearch("");
    setOpenProvider(false);
  };

  const toggleProviderDropdown = () => {
    setOpenProvider((prev) => {
      if (prev) setProviderSearch("");
      return !prev;
    });
  };

  const handleContinueAfterVerify = () => {
    if (verifiedCustomer?.customerName) {
      setElectricCustomerName(verifiedCustomer.customerName);
    }
    setOpenElectric(false);
    setOpenElectricPreview(true);
  };

  const handleProceed = () => {
    if (!validate()) return;
    completeCheck.mutate({
      meterNumber: meterNo,
      providerSlug: productSlug,
    });
  };

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load electricity providers");
    }
  }, [isError]);

  useEffect(() => {
    if (providersResponse === undefined) return;
    if (!providers.length) {
      toast.error("No electricity providers available");
    }
  }, [providers.length, providersResponse]);

  const providerLogo = productSlug
    ? getProviderLogo(
        selectedProvider?.slug ?? productSlug,
        selectedProvider?.logoUrl ?? null
      )
    : "";

  return (
    <div className="fixed inset-0 flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:w-2/5 xxl:w-1/3">
        <div className="flex w-full items-center justify-start gap-6">
          <button type="button" onClick={() => setOpenElectric(false)}>
            <BsArrowLeft className="text-[20px] text-black dark:text-white" />
          </button>
          <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
            Buy Electricity
          </h4>
        </div>

        <div className="mt-10 w-full">
          <PaymentSourceSelect
            value={paymentSource}
            onChange={setPaymentSource}
          />

          <div className="relative mt-6 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">
              Service provider
            </h4>
            <button
              type="button"
              onClick={toggleProviderDropdown}
              className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 px-4 py-3 text-[14px] shadow dark:border-gray-700"
            >
              {providerName ? (
                <div className="flex items-center gap-2">
                  <ProviderAvatar
                    name={providerName}
                    logoUrl={providerLogo || null}
                    size={24}
                  />
                  <span className="text-left text-[13px] font-medium text-gray-500 dark:text-gray-400">
                    {providerName}
                  </span>
                </div>
              ) : (
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">
                  {loadingProviders
                    ? "Loading providers..."
                    : "Select provider"}
                </span>
              )}
              <FaCaretDown className="pointer-events-none text-[20px] text-gray-900 dark:text-gray-200" />
            </button>

            {openProvider && (
              <div className="absolute z-50 mt-2 w-full rounded-xl bg-white p-2 shadow-lg dark:bg-gray-800">
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-600">
                  <RiSearchLine className="h-4 w-4 shrink-0 text-gray-400" />
                  <input
                    type="search"
                    value={providerSearch}
                    onChange={(e) => setProviderSearch(e.target.value)}
                    placeholder="Search provider..."
                    autoFocus
                    className="w-full bg-transparent text-[13px] text-gray-700 outline-none placeholder:text-gray-400 dark:text-gray-300"
                  />
                </div>
                <div className="mt-2 max-h-[280px] overflow-auto">
                  {loadingProviders ? (
                    <p className="p-4 text-center text-[13px] text-gray-500">
                      Loading...
                    </p>
                  ) : filteredProviders.length === 0 ? (
                    <p className="p-4 text-center text-[13px] text-gray-500">
                      No providers found
                    </p>
                  ) : (
                    filteredProviders.map((provider) => {
                      const logo = getProviderLogo(
                        provider.slug,
                        provider.logoUrl
                      );
                      return (
                        <button
                          key={provider.id}
                          type="button"
                          onClick={() => handleProviderSelect(provider)}
                          className="mt-2 flex w-full cursor-pointer items-center justify-between rounded-xl border border-gray-200 p-4 dark:border-gray-600"
                        >
                          <div className="flex items-center gap-3">
                            <ProviderAvatar
                              name={provider.name}
                              logoUrl={logo || null}
                              size={24}
                            />
                            <span className="text-left text-[13px] font-medium text-gray-500 dark:text-gray-400">
                              {provider.name}
                            </span>
                          </div>
                          <SlArrowRight className="text-[15px] text-gray-600 dark:text-gray-400" />
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedProvider && selectedProvider.products.length > 1 && (
            <div className="mt-6">
              <h4 className="text-[14px] font-bold text-gray-500">
                Meter type
              </h4>
              <div className="mt-3 flex gap-3">
                {selectedProvider.products.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => {
                      setProductSlug(product.id);
                      setVerifiedCustomer(null);
                    }}
                    className={`h-[40px] flex-1 rounded-lg text-[14px] font-semibold transition ${
                      productSlug === product.id
                        ? "bg-secondary_button text-white"
                        : "border border-gray-200 bg-transparent text-gray-600 dark:border-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <h4 className="text-[14px] font-bold text-gray-500">
              Meter number
            </h4>
            <input
              type="text"
              inputMode="numeric"
              placeholder="Enter meter number"
              value={meterNo}
              onChange={(e) => {
                setMeterNo(e.target.value.replace(/\D/g, ""));
                setVerifiedCustomer(null);
              }}
              className="mt-3 h-[45px] w-full rounded-[4px] border border-[#EAEAEA] bg-transparent p-3 text-left text-[13px] text-gray-700 shadow outline-none dark:border-gray-700 dark:text-gray-300"
            />
          </div>

          <div className="mt-6">
            <h4 className="text-[14px] font-bold text-gray-500">
              Phone number
            </h4>
            <input
              type="tel"
              placeholder="Enter phone number"
              value={electricPhone}
              maxLength={11}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value.length > 11) value = value.slice(0, 11);
                if (value === "" || value[0] === "0") {
                  setElectricPhone(value);
                }
              }}
              className="mt-3 h-[45px] w-full rounded-[4px] border border-[#EAEAEA] bg-transparent p-3 text-left text-[13px] text-gray-700 shadow outline-none dark:border-gray-700 dark:text-gray-300"
            />
          </div>

          {verifiedCustomer && (
            <div className="mt-6">
              <h4 className="mb-2 text-[14px] font-bold text-gray-500">
                Verified customer details
              </h4>
              <div className="mt-2">
                <h4 className="text-[12px] font-bold text-gray-500">
                  Customer name
                </h4>
                <div className="mt-2 min-h-[45px] w-full rounded-[4px] border border-[#EAEAEA] bg-transparent p-3 text-left text-[13px] text-gray-700 shadow dark:border-gray-700 dark:text-gray-300">
                  {verifiedCustomer.customerName ?? "—"}
                </div>
              </div>
              <div className="mt-4">
                <h4 className="text-[12px] font-bold text-gray-500">
                  Meter number
                </h4>
                <div className="mt-2 min-h-[45px] w-full rounded-[4px] border border-[#EAEAEA] bg-transparent p-3 text-left text-[13px] text-gray-700 shadow dark:border-gray-700 dark:text-gray-300">
                  {verifiedCustomer.meterNumber ?? meterNo}
                </div>
              </div>
              <button
                type="button"
                onClick={handleContinueAfterVerify}
                className="mt-6 h-[52px] w-full rounded-xl bg-secondary_button text-[15px] font-medium text-white"
              >
                Continue
              </button>
            </div>
          )}

          {!verifiedCustomer && (
            <>
              <div className="mt-8 w-full">
                <h4 className="text-[14px] font-bold text-gray-500">Amount</h4>
                <div className="mt-3 flex w-full items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 shadow dark:border-gray-700">
                  <h4 className="text-[13px] font-bold text-black dark:text-white">
                    NGN
                  </h4>
                  <div className="w-[40%]">
                    <div className="flex w-full items-center justify-end text-gray-900 dark:text-gray-200">
                      <span className="font-sora text-[18px] font-bold">₦</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={displayAmount}
                        onChange={handleChange}
                        style={{
                          width: `${Math.max(displayAmount.length, 4)}ch`,
                        }}
                        className="bg-transparent text-right text-[18px] font-semibold outline-none"
                      />
                    </div>
                    {paymentSource === "USDT" && (
                      <h4 className="mt-1 pr-2 text-right text-[12px] font-medium text-gray-500">
                        {displayAmount
                          ? `${formatAmount(
                              Number(electricAmount) / Number(usdtRate)
                            )} USDT`
                          : "0.00 USDT"}
                      </h4>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between">
                {NAIRA_WALLET_ENABLED && (
                  <h4 className="text-[12px] font-bold text-[#BE6B0A]">
                    Balance:{" "}
                    {paymentSource === "NGN"
                      ? `₦${formatAmount(ngnBalance)}`
                      : `${formatAmount(usdtBalance)} USDT`}
                  </h4>
                )}
                {!isBalanceSufficient && electricAmount && (
                  <h4 className="text-[12px] font-bold italic text-red-600">
                    Insufficient funds
                  </h4>
                )}
              </div>

              <button
                type="button"
                onClick={handleProceed}
                disabled={
                  !isBalanceSufficient ||
                  !electricAmount ||
                  !meterNo ||
                  !electricPhone ||
                  !providerName ||
                  !productSlug ||
                  completeCheck.isPending ||
                  loadingProviders
                }
                className={`mt-12 h-[52px] w-full rounded-xl text-[15px] font-medium ${
                  !isBalanceSufficient ||
                  !electricAmount ||
                  !meterNo ||
                  !electricPhone ||
                  !providerName ||
                  !productSlug ||
                  completeCheck.isPending ||
                  loadingProviders
                    ? "cursor-not-allowed bg-gray-400 text-gray-100"
                    : "bg-secondary_button text-white"
                }`}
              >
                {completeCheck.isPending ? (
                  <ClipLoader color="#FFFFFF" size={30} />
                ) : (
                  "Verify Meter Details"
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyElectricity;
